import { Host, h } from "@stencil/core";
import { getScrollableParentToAttachInfiniteScroll } from "./utils";
/**
 * Due to floating point precision errors, we have to ensure a safe threshold
 * to update the scroll position to the bottom.
 */
const PRECISION_OFFSET = 2;
export class ChInfiniteScroll {
    constructor() {
        this.autoScroll = "at-scroll-end";
        this.dataProvider = false;
        this.disabled = false;
        this.infiniteThresholdReachedCallback = undefined;
        this.loadingState = undefined;
        this.position = "bottom";
        this.threshold = "150px";
    }
    // Stored values
    #lastClientHeight = 0;
    #lastScrollHeight = 0;
    #lastScrollTop = 0;
    // Observers
    #ioWatcher;
    #resizeWatcher;
    #abortController; // Allocated at runtime to save resources
    // Refs
    #smartGridRef;
    #scrollableParent;
    #typeOfParentElementAttached = "other";
    el;
    dataProviderChanged(hasDataProvider) {
        if (hasDataProvider) {
            // Wait until the main thread has rendered the UI
            requestAnimationFrame(this.#setInfiniteScroll);
        }
        else {
            this.#disconnectInfiniteScroll();
        }
    }
    disabledChanged(isDisabled) {
        if (isDisabled) {
            this.#disconnectInfiniteScroll();
        }
        else {
            // Wait until the main thread has rendered the UI
            requestAnimationFrame(this.#setInfiniteScroll);
        }
    }
    loadingStateChanged(newValue) {
        this.#checkIfCanFetchMoreData();
        if (newValue === "initial") {
            this.#lastClientHeight = 0;
            this.#lastScrollHeight = 0;
            this.#lastScrollTop = 0;
        }
    }
    thresholdChanged() {
        this.#checkIfCanFetchMoreData();
    }
    #canFetch = () => this.loadingState === "more-data-to-fetch";
    /**
     * This function unobserves and re-observes the infinite scroll element when
     * new items are added in the grid. Without this configuration, if the grid
     * has no scroll even after new items are added, the intersection observer
     * won't fire a new interruption because it is still visible in the viewport.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #checkIfCanFetchMoreData = () => {
        this.#disconnectInfiniteScroll();
        // Wait until the main thread has rendered the UI
        requestAnimationFrame(this.#setInfiniteScroll);
    };
    #emitInfiniteEvent = () => {
        if (this.loadingState !== "loading") {
            // Ensure the infinite scroll is not triggered twice
            this.loadingState = "loading";
            this.infiniteThresholdReachedCallback();
        }
    };
    #setInfiniteScroll = () => {
        // The observer was already set
        if (!this.dataProvider || this.disabled) {
            return;
        }
        // Track the threshold changes after the DOM is updated
        requestAnimationFrame(() => {
            // The ioWatcher must be checked inside the RAF, to avoid memory issues,
            // due to dispatching multiple #setInfiniteScroll, without the watcher defined
            if (!this.#canFetch() || this.#ioWatcher) {
                return;
            }
            const options = {
                root: this.#scrollableParent,
                rootMargin: this.threshold
            };
            this.#ioWatcher = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    this.#emitInfiniteEvent();
                }
            }, options);
            this.#ioWatcher.observe(this.el);
        });
    };
    #attachScroll = () => {
        this.#abortController ??= new AbortController();
        this.#scrollableParent.addEventListener("scroll", this.#trackLastScrollTop, { capture: true, passive: true, signal: this.#abortController.signal });
    };
    #trackLastScrollTop = () => {
        this.#lastScrollTop = this.#scrollableParent.scrollTop;
        this.#lastScrollHeight = this.#scrollableParent.scrollHeight;
    };
    #setInverseLoading = () => {
        // Inverse loading is not supported when the scroll is attached to the window.
        // The current implementation "supports" this scenario, but since this use
        // case changes the position of the scroll every time the grid retrieves
        // data, unexpected behaviors will occur.
        // Also, Android does not support Inverse Loading in this scenario either.
        if (this.#typeOfParentElementAttached === "window" ||
            this.position !== "top") {
            return;
        }
        // Attach scroll after the DOM is rendered
        requestAnimationFrame(this.#attachScroll);
        /**
         * This element represents the cell container (`[slot="grid-content"]`).
         * ```tsx
         *   <ch-smart-grid>
         *     #shadow-root (open)
         *     |  <ch-infinite-scroll></ch-infinite-scroll>
         *     |  <slot name="grid-content"></slot>
         *     <div slot="grid-content">
         *       <ch-smart-grid-cell>...</ch-smart-grid-cell>
         *       <ch-smart-grid-cell>...</ch-smart-grid-cell>
         *       ...
         *     </div>
         *   </ch-smart-grid>
         * ```
         */
        const overflowingContent = this.#smartGridRef.querySelector("[slot='grid-content']");
        overflowingContent.scrollTop =
            overflowingContent.scrollHeight + PRECISION_OFFSET;
        this.#resizeWatcher = new ResizeObserver(this.#adjustInverseScrollPositionWhenContentSizeChanges);
        this.#resizeWatcher.observe(overflowingContent);
        this.#resizeWatcher.observe(this.#scrollableParent);
    };
    #adjustInverseScrollPositionWhenContentSizeChanges = () => {
        // console.log("adjustInverseScrollPositionWhenContentSizeChanges...");
        // Current values
        const currentClientHeight = this.#scrollableParent.clientHeight;
        const currentScrollHeight = this.#scrollableParent.scrollHeight;
        const firstTimeThatContentOverflows = this.#lastClientHeight === this.#lastScrollHeight &&
            currentClientHeight < currentScrollHeight;
        // Must set the scroll at the bottom position
        if (firstTimeThatContentOverflows) {
            const newScrollTop = currentScrollHeight - currentClientHeight + PRECISION_OFFSET;
            this.#lastClientHeight = currentClientHeight;
            this.#lastScrollHeight = currentScrollHeight;
            // Scroll to bottom
            this.#scrollableParent.scrollTop = newScrollTop;
            this.#lastScrollTop = newScrollTop;
            return;
        }
        const scrollWasAtTheBottom = this.#lastScrollHeight <=
            this.#lastClientHeight + this.#lastScrollTop + PRECISION_OFFSET;
        // The scroll is only adjusted if the grid has a data provider or the
        // scroll was at the bottom position (and has to keep the scroll at the
        // bottom).
        // When the grid has a data provider items can be loaded via infinite
        // scroll, so the scroll position needs adjusted when new items are added
        if (this.dataProvider ||
            (this.autoScroll === "at-scroll-end" && scrollWasAtTheBottom)) {
            const scrollOffset = currentScrollHeight - this.#lastScrollHeight;
            const clientHeightOffset = currentClientHeight < this.#lastClientHeight
                ? this.#lastClientHeight - currentClientHeight
                : 0;
            const newScrollTop = this.#lastScrollTop +
                scrollOffset +
                clientHeightOffset +
                (scrollWasAtTheBottom ? PRECISION_OFFSET : 0); // Scroll to bottom
            this.#scrollableParent.scrollTop = newScrollTop;
            this.#lastScrollTop = newScrollTop;
        }
        this.#lastClientHeight = currentClientHeight;
        this.#lastScrollHeight = currentScrollHeight;
    };
    #disconnectInverseLoading = () => {
        this.#resizeWatcher?.disconnect();
        this.#resizeWatcher = undefined;
        // Remove scroll events in the smart grid
        this.#abortController.abort();
    };
    #disconnectInfiniteScroll = () => {
        this.#ioWatcher?.disconnect();
        this.#ioWatcher = undefined;
    };
    componentDidLoad() {
        this.#smartGridRef = this.el.getRootNode()
            .host;
        const result = getScrollableParentToAttachInfiniteScroll(this.#smartGridRef);
        this.#typeOfParentElementAttached = result[0];
        this.#scrollableParent = result[1];
        this.#setInverseLoading();
        // Wait until the main thread has rendered the UI
        requestAnimationFrame(this.#setInfiniteScroll);
    }
    disconnectedCallback() {
        this.#disconnectInfiniteScroll();
        this.#disconnectInverseLoading();
    }
    // TODO: Ensure the ch-infinite-scroll doesn't crash when is only used by
    // itself (without a ch-smart-grid as a parent)
    render() {
        return (h(Host, { key: '7776383ec613bbb3107cb741b1d828763506a953', class: this.loadingState === "loading" ? "loading" : undefined, "aria-hidden": "true" }, this.loadingState === "loading" && h("slot", { key: '486f52b30e53df0055b6e9ecdf24deaba4ebadb6' })));
    }
    static get is() { return "ch-infinite-scroll"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["infinite-scroll.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["infinite-scroll.css"]
        };
    }
    static get properties() {
        return {
            "autoScroll": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"never\" | \"at-scroll-end\"",
                    "resolved": "\"at-scroll-end\" | \"never\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the scroll position will be adjusted when the content size\nchanges when using `position = \"bottom\"`.\n  - \"at-scroll-end\": If the scroll is positioned at the end of the content,\n  the infinite-scroll will maintain the scroll at the end while the\n  content size changes.\n\n - \"never\": The scroll position won't be adjusted when the content size\n  changes."
                },
                "attribute": "auto-scroll",
                "reflect": false,
                "defaultValue": "\"at-scroll-end\""
            },
            "dataProvider": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the infinite scroll is used in a grid that has data provider.\nThis attribute determine the utility of the infinite scroll, because in\ncertain configurations the infinite scroll can be used only to implement\nthe inverse loading utility."
                },
                "attribute": "data-provider",
                "reflect": false,
                "defaultValue": "false"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the infinite scroll is disabled. When disabled, the infinite\nscroll won't fire any event when reaching the threshold.\nThe `dataProvider` property can be `true` and this property can be `false`\nat the same time, meaning that the infinite scroll is disabled, but if the\ncontrol has `inverseLoading`, the `dataProvider` property will re-position\nthe scrollbar when new content is added to the grid."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "infiniteThresholdReachedCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "() => void",
                    "resolved": "() => void",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This callback will be called every time the `threshold` is reached.\n\nWhen the threshold is met and this callback is executed, the internal\n`loadingState` will be changed to `\"loading\"` and the user has to keep in\nsync the `loadingState` of the component with the real state of the data."
                }
            },
            "loadingState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "SmartGridDataState",
                    "resolved": "\"all-records-loaded\" | \"initial\" | \"loading\" | \"more-data-to-fetch\"",
                    "references": {
                        "SmartGridDataState": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/smart-grid/internal/infinite-scroll/types.ts::SmartGridDataState"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `\"more-data-to-fetch\"`, the infinite scroll will execute the\n`infiniteThresholdReachedCallback` when the `threshold` is met. When the\nthreshold is met, the internal `loadingState` will be changed to\n`\"loading\"` and the user has to keep in sync the `loadingState` of the\ncomponent with the real state of the data.\n\nSet this to `\"all-records-loaded\"` to disable the infinite scroll from\nactively trying to receive new data while reaching the threshold. This is\nuseful when it is known that there is no more data that can be added, and\nthe infinite scroll is no longer needed."
                },
                "attribute": "loading-state",
                "reflect": false
            },
            "position": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"top\" | \"bottom\"",
                    "resolved": "\"bottom\" | \"top\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The position of the infinite scroll element.\nThe value can be either `top` or `bottom`. When `position === \"top\"`, the\ncontrol also implements inverse loading."
                },
                "attribute": "position",
                "reflect": false,
                "defaultValue": "\"bottom\""
            },
            "threshold": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The threshold distance from the bottom of the content to call the\n`infinite` output event when scrolled.\nThe threshold value can be either a percent, or in pixels. For example,\nuse the value of `10%` for the `infinite` output event to get called when\nthe user has scrolled 10% from the bottom of the page. Use the value\n`100px` when the scroll is within 100 pixels from the bottom of the page."
                },
                "attribute": "threshold",
                "reflect": false,
                "defaultValue": "\"150px\""
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "dataProvider",
                "methodName": "dataProviderChanged"
            }, {
                "propName": "disabled",
                "methodName": "disabledChanged"
            }, {
                "propName": "loadingState",
                "methodName": "loadingStateChanged"
            }, {
                "propName": "threshold",
                "methodName": "thresholdChanged"
            }];
    }
}
